import { sanitizeRevitParameterNames } from '../../../shared/lib/revitParameterNames';
import type {
  MappingParameterScopeRequirement,
  MappingParameterScopeSatisfaction,
} from './mappingReadinessFamilyExclusions';
import type { MappingProjectReadinessDiff } from './mappingProjectReadiness';

export function selectMappingOnlyRepairParameterNames(
  diff: MappingProjectReadinessDiff
): string[] {
  // Dedicated setup areas own their categories and binding policies. Sending
  // those names through Mapping's catch-all mutator would smear their scope
  // across every piping category.
  return diff.repairable
    .filter(
      (reference) =>
        reference.repairKinds.includes('mapping') &&
        !reference.repairKinds.some((kind) => kind !== 'mapping')
    )
    .map((reference) => reference.name);
}

export function buildUnsatisfiedProcessProjectParameterScope(
  requirements: MappingParameterScopeRequirement[],
  satisfaction: MappingParameterScopeSatisfaction[]
): {
  processConditionParameterNames: string[];
  projectPropertyParameterNames: string[];
} {
  const satisfiedRequirementKeys = new Set(
    satisfaction
      .filter((result) => result.satisfied)
      .map((result) => result.requirementKey)
  );
  const conflictedRequirementKeys = new Set(
    satisfaction
      .filter((result) => 'status' in result && result.status === 'conflict')
      .map((result) => result.requirementKey)
  );
  const selectUnsatisfiedNames = (area: 'process' | 'project') =>
    sanitizeRevitParameterNames(
      requirements
        .filter(
          (requirement) =>
            requirement.area === area &&
            !satisfiedRequirementKeys.has(requirement.key) &&
            !conflictedRequirementKeys.has(requirement.key)
        )
        .map((requirement) => requirement.parameterName)
    );

  return {
    processConditionParameterNames: selectUnsatisfiedNames('process'),
    projectPropertyParameterNames: selectUnsatisfiedNames('project'),
  };
}
